// ─────────────────────────────────────────────────────────────────────────────
// Client for the local "WordCraft NFC Agent" — a small helper the operator runs
// on the PC with the ACR1252U reader. The browser can't drive a PC/SC USB reader
// directly, so the agent exposes ws://127.0.0.1:47821 and we talk to it here.
// 127.0.0.1 is a potentially-trustworthy origin, so this ws:// is allowed from
// our HTTPS admin panel without mixed-content blocking.
// ─────────────────────────────────────────────────────────────────────────────

const AGENT_URL = 'ws://127.0.0.1:47821';
const RECONNECT_MS = 4000;
const WRITE_TIMEOUT_MS = 60000;

// What the agent read off the tag currently on the reader.
export interface NfcTagInfo {
  state: 'empty' | 'written' | 'unreadable';
  uri: string | null; // the decoded link when state==='written' (null if not a URI)
}

export interface NfcAgentState {
  connected: boolean; // WS connected to the agent
  reader: string | null; // reader name, or null if none plugged in
  // tag currently on the reader; `ndef` is null until the agent has inspected it
  card: { atr: string; type: string; ndef: NfcTagInfo | null } | null;
}

export type NfcWriteResult =
  | { ok: true; tag: string; bytes?: number } // bytes present for write, absent for erase
  | { ok: false; message: string };

type Listener = (s: NfcAgentState) => void;

class NfcAgentClient {
  private ws: WebSocket | null = null;
  private state: NfcAgentState = { connected: false, reader: null, card: null };
  private listeners = new Set<Listener>();
  private waiters = new Map<number, (r: NfcWriteResult) => void>();
  private nextId = 1;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refCount = 0;

  /** Called by a component that wants a live agent connection. */
  acquire(): void {
    this.refCount += 1;
    if (this.refCount === 1) this.connect();
  }

  /** Release when the component unmounts; closes the socket when nobody needs it. */
  release(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount === 0) this.stop();
  }

  getState(): NfcAgentState {
    return this.state;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    l(this.state);
    return () => {
      this.listeners.delete(l);
    };
  }

  private connect(): void {
    if (this.ws) return;
    let sock: WebSocket;
    try {
      sock = new WebSocket(AGENT_URL);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = sock;
    sock.onmessage = (ev) => this.onMessage(ev);
    sock.onclose = () => {
      if (this.ws === sock) this.ws = null;
      this.setState({ connected: false, reader: null, card: null });
      // Settle any in-flight write() so the UI fails immediately instead of
      // hanging until the write timeout.
      this.waiters.forEach((w) =>
        w({ ok: false, message: 'Зʼєднання з NFC-агентом втрачено' }),
      );
      this.waiters.clear();
      if (this.refCount > 0) this.scheduleReconnect();
    };
    sock.onerror = () => {
      try {
        sock.close();
      } catch {
        /* ignore */
      }
    };
  }

  private stop(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.onclose = null;
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.setState({ connected: false, reader: null, card: null });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.refCount === 0) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.refCount > 0) this.connect();
    }, RECONNECT_MS);
  }

  private onMessage(ev: MessageEvent): void {
    let msg: any;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (msg.type === 'hello') {
      this.setState({ ...this.state, connected: true });
    } else if (msg.type === 'status') {
      this.setState({
        connected: true,
        reader: msg.reader ?? null,
        card: msg.card ?? null,
      });
    } else if (msg.type === 'written') {
      this.resolve(msg.id, { ok: true, tag: msg.tag, bytes: msg.bytes });
    } else if (msg.type === 'erased') {
      this.resolve(msg.id, { ok: true, tag: msg.tag });
    } else if (msg.type === 'error') {
      this.resolve(msg.id, {
        ok: false,
        message: msg.message || 'Помилка запису на мітку',
      });
    }
    // 'waiting' / 'writing' / 'erasing' / 'pong' — no-op (UI shows the in-progress state).
  }

  private resolve(id: number, r: NfcWriteResult): void {
    const w = this.waiters.get(id);
    if (w) {
      this.waiters.delete(id);
      w(r);
    }
  }

  private setState(s: NfcAgentState): void {
    this.state = s;
    this.listeners.forEach((l) => l(s));
  }

  /**
   * Ask the agent to write `url` to the next tag. Resolves when the write
   * completes, fails, or times out. If no tag is present yet, the agent writes
   * as soon as one is tapped.
   */
  write(url: string): Promise<NfcWriteResult> {
    return this.request({ type: 'write', url });
  }

  /**
   * Ask the agent to erase / re-format the next tag to an empty NDEF message
   * (any previously written link is dropped). Same tap-and-wait semantics as
   * {@link write}.
   */
  erase(): Promise<NfcWriteResult> {
    return this.request({ type: 'erase' });
  }

  /** Send a tag operation to the agent and await its result (write or erase). */
  private request(payload: Record<string, unknown>): Promise<NfcWriteResult> {
    return new Promise((resolve) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        resolve({ ok: false, message: 'Агент NFC не підключено' });
        return;
      }
      const id = this.nextId++;
      this.waiters.set(id, resolve);
      this.ws.send(JSON.stringify({ ...payload, id }));
      setTimeout(() => {
        if (this.waiters.has(id)) {
          this.waiters.delete(id);
          this.cancel(id); // tell the agent to drop this pending request
          resolve({
            ok: false,
            message: 'Час очікування вичерпано — мітку не піднесено.',
          });
        }
      }, WRITE_TIMEOUT_MS);
    });
  }

  /** Cancel a pending write (a specific id, or all if omitted). */
  cancel(id?: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'cancel', id }));
    }
  }
}

export const nfcAgent = new NfcAgentClient();
